import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { productsQuery } from '../api/inventory.ts';
import { addLine, type Quote, quoteQuery, setInclusionDeclined } from '../api/quotes.ts';
import {
  addInclusion,
  type Inclusion,
  inclusionsQuery,
  retireInclusion,
  roomsQuery,
  setInclusionQuantity,
} from '../api/venues.ts';
import { Button } from '../components/button.tsx';
import { AddButton, RemoveButton } from '../components/action-buttons.tsx';

// CB-REQ INV-A14 at v1.983 (venue slice 3A2): what a venue's hire includes. Installed is not
// included: a venue's own item goes on a quote at nothing only as far as its inclusions, venue-wide
// and for the line's room, cover it. Chargeable venue gear waits for venue billing.

/** The venue page's list: whole venue first, then each room, with add, recount and retire. */
export function VenueInclusions({ venueId, mayManage }: { venueId: string; mayManage: boolean }) {
  const inclusions = useQuery(inclusionsQuery(venueId));
  const products = useQuery(productsQuery);
  const [adding, setAdding] = useState(false);
  const own = (products.data ?? []).filter((p) => p.ownerVenueId === venueId);
  const name = new Map(own.map((p) => [p.id, p.name]));
  const all = inclusions.data ?? [];
  return (
    <section aria-labelledby="inclusions" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="inclusions" className="section-title">
          Included in the hire
        </h2>
        {mayManage && !adding && own.length > 0 ? (
          <AddButton
            noun="Include"
            aria-label="Include an item"
            variant="quiet"
            onClick={() => setAdding(true)}
          />
        ) : null}
      </div>
      <p className="text-sm text-ink-soft">
        The venue’s own equipment its hire includes, at no charge. Anything not listed is charged at
        its rate, on a resale or direct-bill job.
      </p>
      {adding ? (
        <InclusionForm venueId={venueId} items={own} onDone={() => setAdding(false)} />
      ) : null}
      {inclusions.isError ? (
        <p role="alert" className="alert">
          {inclusions.error.message}
        </p>
      ) : null}
      {inclusions.data && all.length === 0 ? (
        <p className="text-sm text-ink-soft">
          {own.length === 0
            ? 'Nothing yet. Record the venue’s own equipment in Inventory first.'
            : 'Nothing included yet.'}
        </p>
      ) : null}
      {all.length > 0 ? (
        <ul aria-label="Included in the hire" className="flex flex-col gap-2 text-sm">
          {all.map((i) => (
            <InclusionRow
              key={i.id}
              venueId={venueId}
              inclusion={i}
              itemName={name.get(i.productId) ?? 'Venue equipment'}
              mayManage={mayManage}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function InclusionRow({
  venueId,
  inclusion: i,
  itemName,
  mayManage,
}: {
  venueId: string;
  inclusion: Inclusion;
  itemName: string;
  mayManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [count, setCount] = useState(String(i.quantity));
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: inclusionsQuery(venueId).queryKey });
  const recount = useMutation({
    mutationFn: (n: number) => setInclusionQuantity(venueId, i.id, n),
    onSuccess: refresh,
  });
  const retire = useMutation({
    mutationFn: () => retireInclusion(venueId, i.id),
    onSuccess: refresh,
  });
  const where = i.room?.name ?? 'Whole venue';
  const n = Number(count);
  return (
    <li className="panel flex flex-wrap items-baseline gap-2 p-3">
      <span className="font-semibold">{itemName}</span>
      <span className="text-ink-soft">{where}</span>
      {mayManage ? (
        <>
          <label className="flex items-baseline gap-1">
            <span className="sr-only">
              How many {itemName} ({where})
            </span>
            ×
            <input
              className="input w-16"
              type="number"
              min={1}
              value={count}
              onChange={(e) => setCount(e.currentTarget.value)}
            />
          </label>
          {Number.isInteger(n) && n >= 1 && n !== i.quantity ? (
            <button
              type="button"
              className="link"
              disabled={recount.isPending}
              onClick={() => recount.mutate(n)}
            >
              Save<span className="sr-only"> the count of {itemName}</span>
            </button>
          ) : null}
          <RemoveButton
            what={`${itemName}, ${where}`}
            disabled={retire.isPending}
            onClick={() => retire.mutate()}
          />
        </>
      ) : (
        <span>× {i.quantity}</span>
      )}
      {recount.error || retire.error ? (
        <span role="alert" className="w-full font-semibold text-accent-2-700">
          {(recount.error ?? retire.error)!.message}
        </span>
      ) : null}
    </li>
  );
}

function InclusionForm({
  venueId,
  items,
  onDone,
}: {
  venueId: string;
  items: ReadonlyArray<{ id: string; name: string }>;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const rooms = useQuery(roomsQuery(venueId));
  const [productId, setProductId] = useState(items[0]?.id ?? '');
  const [roomId, setRoomId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [problem, setProblem] = useState<string | undefined>();
  const add = useMutation({
    mutationFn: () =>
      addInclusion(venueId, { productId, roomId: roomId || null, quantity: Number(quantity) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: inclusionsQuery(venueId).queryKey });
      onDone();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const n = Number(quantity);
    if (!Number.isInteger(n) || n < 1) return setProblem('Include at least one.');
    setProblem(undefined);
    add.mutate();
  }

  return (
    <form onSubmit={submit} aria-label="Include an item" className="panel flex flex-col gap-3 p-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inclusion-item" className="text-xs text-ink-soft">
            Item
          </label>
          <select
            id="inclusion-item"
            className="input"
            value={productId}
            onChange={(e) => setProductId(e.currentTarget.value)}
          >
            {items.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inclusion-room" className="text-xs text-ink-soft">
            Included with
          </label>
          <select
            id="inclusion-room"
            className="input"
            value={roomId}
            onChange={(e) => setRoomId(e.currentTarget.value)}
          >
            <option value="">The whole venue</option>
            {(rooms.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inclusion-quantity" className="text-xs text-ink-soft">
            How many
          </label>
          <input
            id="inclusion-quantity"
            className="input"
            type="number"
            min={1}
            value={quantity}
            onChange={(e) => setQuantity(e.currentTarget.value)}
          />
        </div>
      </div>
      {problem || add.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {problem ?? add.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={add.isPending}>
          {add.isPending ? 'Saving…' : 'Include'}
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * The quote's panel: what the venue's hire includes here, each with Add (a $0 line, in its room)
 * while the quote is a draft, and Not included for this booking while it is open. Nothing is held
 * until it is added.
 */
export function QuoteInclusions({ quote: q, mayManage }: { quote: Quote; mayManage: boolean }) {
  if (q.inclusions.length === 0) return null;
  const open = mayManage && (q.status === 'draft' || q.status === 'sent');
  return (
    <section aria-labelledby="venue-includes" className="flex flex-col gap-2">
      <h2 id="venue-includes" className="section-title">
        Included by the venue
      </h2>
      <ul className="flex flex-col gap-2 text-sm">
        {q.inclusions.map((i) => (
          <QuoteInclusionRow
            key={i.id}
            quote={q}
            inclusion={i}
            mayAdd={open && q.status === 'draft'}
            mayDecline={open}
          />
        ))}
      </ul>
    </section>
  );
}

function QuoteInclusionRow({
  quote: q,
  inclusion: i,
  mayAdd,
  mayDecline,
}: {
  quote: Quote;
  inclusion: Quote['inclusions'][number];
  mayAdd: boolean;
  mayDecline: boolean;
}) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: quoteQuery(q.id).queryKey });
  const left = i.quantity - i.onQuote;
  const add = useMutation({
    mutationFn: () =>
      addLine(q.id, {
        productId: i.productId,
        quantity: left,
        ...(i.room ? { roomId: i.room.id } : {}),
      }),
    onSuccess: refresh,
  });
  const decline = useMutation({
    mutationFn: (declined: boolean) => setInclusionDeclined(q.id, i.id, declined),
    onSuccess: refresh,
  });
  const where = i.room?.name ?? 'whole venue';
  return (
    <li className="panel flex flex-wrap items-baseline gap-2 p-3">
      <span className={i.declined ? 'text-ink-soft line-through' : 'font-semibold'}>
        {i.productName} × {i.quantity}
      </span>
      <span className="text-ink-soft">{where}</span>
      {i.declined ? (
        <span className="tag">Not included for this booking</span>
      ) : i.onQuote > 0 ? (
        <span className="text-ink-soft">{i.onQuote} on the quote</span>
      ) : null}
      {mayAdd && !i.declined && left > 0 ? (
        <button
          type="button"
          className="link"
          disabled={add.isPending}
          onClick={() => add.mutate()}
        >
          Add {left}
          <span className="sr-only">
            {' '}
            {i.productName} ({where})
          </span>
        </button>
      ) : null}
      {mayDecline ? (
        <button
          type="button"
          className="link"
          disabled={decline.isPending}
          onClick={() => decline.mutate(!i.declined)}
        >
          {i.declined ? 'Include' : 'Exclude'}
          <span className="sr-only">
            {' '}
            ({i.productName}, {where})
          </span>
        </button>
      ) : null}
      {add.error || decline.error ? (
        <span role="alert" className="w-full font-semibold text-accent-2-700">
          {(add.error ?? decline.error)!.message}
        </span>
      ) : null}
    </li>
  );
}
